"use client"

import { useEffect, useRef, useState } from "react"
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  LinearProgress,
  Link,
  CircularProgress,
} from "@mui/material"
import { Camera, ArrowBack, ArrowForward } from "@mui/icons-material"
import { useUser } from "@/contexts/UserContext"
import { useAvatar } from "@/contexts/AvatarContext"
import { apiRemote, ICountry, useApiCache } from "@/api"
import SelectWithSearch from "@/components/SelectWithSearch"
import { useAlert } from "@/contexts/AlertContext"

interface UserFlowModalProps {
    hasAvatar: any;
    bpUploaderNonce: string;
    bpAvatarCropstoreNonce: string;
    bpDeleteAvatarLinkNonce: string;
    profile: {};
    fetchData: any;
}

export default function UserFlowModal({ hasAvatar, bpUploaderNonce, bpAvatarCropstoreNonce, bpDeleteAvatarLinkNonce, profile, fetchData }: UserFlowModalProps) {
  const [ageGateOpen, setAgeGateOpen] = useState(true)
  const [wizardOpen, setWizardOpen] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [selectedTopics, setSelectedTopics] = useState<string[]>([])
  const { user } = useUser();
  const { avatar, setAvatar } = useAvatar();
  const [countries, setCountries] = useState<ICountry[]>([]);
  const apiCache=useApiCache();
  const [loading, setLoading] = useState(false)
  const [profile1, setProfile1] = useState({
    username: user?.user_login,
    educator_info: {
      headline: "",
      country_id: "",
      is_first_access: 1
    },
    educator_professional: {
      current_role: "",
      primary_organization: "",
    },
    educator_expertise: [],
  } as any);
  const alert = useAlert();

  const [formData, setFormData] = useState({
    username: user?.user_login,
    educator_info: {
      headline: "",
      country_id: "",
      is_first_access: 1
    },
    educator_professional: {
      current_role: "",
      primary_organization: "",
    },
    educator_expertise: [] as any[],
  });
  
  // Tạo ref để truy cập input file
  const fileInputRef = useRef<HTMLInputElement>(null)

  const topics = [
    "Gamification",
    "AIinEducation",
    "ProjectBasedLearning",
    "ClassroomManagement",
    "EdTech",
    "SpecialEducation",
  ]

  const avatarToDisplay = avatar || null

  useEffect(()=>{
    try {
      apiCache.getCountryList().then((data)=>{
        setCountries(data)
      }).catch((err) => {
        console.log(err)
      });
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  },[])

  useEffect(()=>{
    // When the `profile` prop changes, update the `profile1` state.
    // Ensure that if `profile` doesn't have the full structure, we merge it safely.
    setProfile1((prev: any) => ({
      ...prev, // Keep existing profile1 state
      ...profile, // Overlay with new profile prop
      educator_info: {
        ...prev.educator_info, // Keep existing educator_info
        ...(profile as any)?.educator_info, // Overlay with new educator_info from profile prop
      },
      educator_professional: {
        ...prev.educator_professional, // Keep existing educator_professional
        ...(profile as any)?.educator_professional, // Overlay with new educator_professional from profile prop
      },
      educator_expertise: (profile as any)?.educator_expertise || [], // Ensure it's an array
    }));

    // Also update selectedTopics based on profile.educator_expertise if it exists
    if ((profile as any)?.educator_expertise) {
      setSelectedTopics((profile as any).educator_expertise.map((item: any) => item.area_of_expertise));
    }

  },[profile])

  const handleAgeGateAgree = () => {
    setAgeGateOpen(false)
    setWizardOpen(true)
  }

  const handleNext = () => {
    if (activeStep < 2) {
      setActiveStep(activeStep + 1)
    } else {
      setLoading(true);
      try {
        const newProfile = {
          ...profile1,
          educator_info: {
            ...profile1.educator_info,
            is_first_access: 1,
          },
        };
        setProfile1(newProfile);
        apiRemote.updateProfesionalInfo(profile1).then((data)=>{
          console.log(data)
          if(data.code == 200){
            setWizardOpen(false)
            fetchData()
            alert.success("Updated Successfully!")
          }
        }).catch((e)=>{
          console.log(e)
        })

      } catch (error) {
        // alert.error("Failed to update profile. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  }

  const handleBack = () => {
    setActiveStep(activeStep - 1)
  }

  const handleFinish = () => {
    setWizardOpen(false)
    const newProfile = {
      ...profile1,
      educator_info: {
        ...profile1.educator_info,
        is_first_access: 1,
      },
    };
    setProfile1(newProfile);
    apiRemote.updateProfesionalInfo(newProfile).then((data)=>{
      console.log(data)
    }).catch((e)=>{
      console.log(e)
    })
  }

  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  // Hàm xử lý khi người dùng chọn file
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const validExtensions = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validExtensions.includes(file.type)) {
        alert.error('Only accept files in .jpg, .jpeg, .png formats');
        return;
      }
    }

    if (!file) return;

    // Validate file size (5MB = 5 * 1024 * 1024 bytes)
    const maxFileSize = 5 * 1024 * 1024; // 5MB
    if (file && file.size > maxFileSize) {
      alert.error('File size is too large. Please select a file smaller than 5MB.');
      event.target.value = ''; // Clear the input
      return;
    }
    
    try {
      const bp_params = {
        object: 'user',
        item_id: user?.ID,
        has_avatar: hasAvatar,
        nonces: {
          set: bpAvatarCropstoreNonce,
          remove: bpDeleteAvatarLinkNonce
        },
        ui_available_width: 788
      };
      const action = 'bp_avatar_upload';
      const fileName = file.name;
      apiRemote
        .uploadAvatar({
          avatar: file,
          nonce: bpUploaderNonce,
          bp_params,
          action,
          name: file.name,
        })
        .then((res) => {
          // alert.success(res.update_message);
          if (res.data.avatar != "") {
            setAvatar(res.data.avatar);
          }
        })
        .catch((err) => {
          console.error("Update failed:", err);
        });
    } catch (error) {
      console.error("Error updating avatar:", error);
    } finally {
    }
  }

  const handleTopicToggle = (topic: string) => {
    setSelectedTopics((prev) => {
      const newTopics = prev.includes(topic)
        ? prev.filter((t) => t !== topic)
        : [...prev, topic];

      setProfile1((prevData: any) => ({
        ...prevData,
        educator_expertise: newTopics.map((t) => ({ area_of_expertise: t })),
      }));

      return newTopics;
    });
  };

  return (
    <>
      {/* Age Gate Modal */}
      <Dialog
        open={ageGateOpen}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, p: 2 },
        }}
      >
        <DialogContent sx={{ textAlign: "center", py: 5 }}>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Welcome to the Pythaverse Community
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
            This is a professional network for educators. By clicking 'Agree & Continue', you confirm that you are{" "}
            <strong>18 years of age or older</strong> and agree to our{" "}
            <Link href={`${process.env.URL_COMMUNITY_GUILINE}`} color="primary" target="_blank" fontWeight={500}>
              Community Guidelines
            </Link>
            .
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleAgeGateAgree}
            sx={{
              bgcolor: "#5A48A5",
              "&:hover": { bgcolor: "#4a3a8a" },
              borderRadius: 2,
              px: 4,
              py: 1.5,
            }}
          >
            Agree & Continue
          </Button>
        </DialogContent>
      </Dialog>

      {/* Profile Setup Wizard */}
      <Dialog
        open={wizardOpen}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, p: 2 },
        }}
      >
        <DialogContent sx={{ py: 5 }}>
          <Typography variant="h4" gutterBottom fontWeight={600} textAlign="center">
            Set Up Your Professional Profile
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
            This helps others in the community connect with you.
          </Typography>

          <LinearProgress
            variant="determinate"
            value={((activeStep + 1) / 3) * 100}
            sx={{ mb: 3, height: 8, borderRadius: 4 }}
          />

          {/* Step 1: Essentials */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                Step 1: The Essentials
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <Box
                  onClick={handleAvatarClick}
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    bgcolor: "#f5f7fa",
                    border: "2px dashed #e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "#e2e8f0" },
                    backgroundImage: avatarToDisplay ? `url(${avatarToDisplay})` : "none",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  {/* Chỉ hiển thị icon camera khi không có ảnh đại diện */}
                  {!avatarToDisplay && <Camera sx={{ fontSize: 40, color: "text.secondary" }} />}
                </Box>
                {/* Input file ẩn, không hiển thị trên giao diện */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  accept="image/*"
                />
              </Box>
              <TextField
                fullWidth
                label="Your Headline"
                placeholder="e.g., High School Physics Teacher & AI Enthusiast"
                variant="outlined"
                sx={{ mb: 2 }}
                value={profile1?.educator_info?.headline}
                onChange={(e) =>
                  setProfile1((prev: any) => ({
                    ...prev,
                    educator_info: { ...prev.educator_info, headline: e.target.value },
                  }))
                }
              />
            </Box>
          )}

          {/* Step 2: Professional Context */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                Step 2: Professional Context
              </Typography>
              <TextField
                fullWidth
                label="Current Role/Title"
                placeholder="e.g., Lead Teacher"
                variant="outlined"
                sx={{ mb: 2 }}
                value={profile1?.educator_professional?.current_role}
                onChange={(e) =>
                  setProfile1((prev: any) => ({
                    ...prev,
                    educator_professional: { ...prev.educator_professional, current_role: e.target.value },
                  }))
                }
              />
              <TextField
                fullWidth
                label="School / Organization"
                placeholder="e.g., Northwood High"
                variant="outlined"
                sx={{ mb: 2 }}
                value={profile1?.educator_professional?.primary_organization}
                onChange={(e) =>
                  setProfile1((prev: any) => ({
                    ...prev,
                    educator_professional: { ...prev.educator_professional, primary_organization: e.target.value },
                  }))
                }
              />
              <FormControl fullWidth>
                <SelectWithSearch
                  options={countries.map((item) => ({
                    value: item.id.toString(),
                    label: item.country_name,
                  }))}
                  label={"Country"}
                  value={profile1?.educator_info?.country_id}
                  required={false}
                  disabled={false}
                  onChange={(value) => {
                    setProfile1((prev: any) => ({
                      ...prev,
                      educator_info: { ...prev.educator_info, country_id: value.value.toString() },
                    }));
                  }}
                />
              </FormControl>
            </Box>
          )}

          {/* Step 3: Areas of Interest */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                Step 3: Areas of Interest
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Follow a few topics to personalize your feed.
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                {topics.map((topic) => (
                  <Chip
                    key={topic}
                    label={`#${topic}`}
                    onClick={() => handleTopicToggle(topic)}
                    color={selectedTopics.includes(topic) ? "primary" : "default"}
                    variant={selectedTopics.includes(topic) ? "filled" : "outlined"}
                    sx={{
                      borderRadius: 5,
                      "&:hover": {
                        bgcolor: selectedTopics.includes(topic) ? "primary.dark" : "primary.main",
                        color: "#000",
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Navigation */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 4 }}>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0}
              startIcon={<ArrowBack />}
              sx={{ visibility: activeStep === 0 ? "hidden" : "visible" }}
            >
              Back
            </Button>
            <Link
              href="#"
              onClick={(e) => {
                e.preventDefault()
                handleFinish()
              }}
              color="text.secondary"
              sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
            >
              Skip for Now
            </Link>
            <Button 
              variant="contained" 
              disabled={loading}
              onClick={handleNext}
              endIcon={activeStep < 2 ? <ArrowForward /> : undefined}
              sx={{
                bgcolor: "#5A48A5",
                "&:hover": { bgcolor: "#4a3a8a" },
              }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  activeStep < 2 ? "Next" : "Finish Setup"
                )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}
